import { useEffect, useState } from 'react';
import {
  DeliveryRow, RevisionSummary, SubmissionNote, SubmissionRow, getSubmissionRevision,
} from '../../api/forms';
import type { Submitter } from '../../shared/types';
import { formatFull } from './submissionColumns';

interface Entry {
  at: string;
  kind: 'edit' | 'delivered' | 'failed' | 'original' | 'note' | 'other';
  title: string;
  who: string;
  revision?: number;
  error?: string;
  /** a note's own words, and whether this person may take them back */
  body?: string;
  noteId?: number;
  canDelete?: boolean;
}

/**
 * Answers, deliveries and the team's notes in ONE thread, newest first.
 *
 * Separating them would leave the questions a person actually has — "what
 * changed?", "did it get there?", "what did we say about it?" — in different
 * places, when the answer to one is usually the reason for another.
 */
export default function SubmissionHistory({ submission, deliveries, revisions, notes = [], onRestore, onDeleteNote }: {
  submission: SubmissionRow;
  deliveries: DeliveryRow[];
  revisions: RevisionSummary[];
  /** triage (P5) — the team's notes, interleaved by time */
  notes?: SubmissionNote[];
  onRestore?: (data: Record<string, unknown>) => void;
  onDeleteNote?: (noteId: number) => void;
}) {
  const [diffs, setDiffs] = useState<Record<number, { key: string; before: unknown; after: unknown }[]>>({});
  const [snapshots, setSnapshots] = useState<Record<number, Record<string, unknown>>>({});

  // Snapshots, so a diff is computed between adjacent ones at read time rather
  // than replayed from stored deltas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const wanted = revisions.map((r) => r.revision);
      const loaded: Record<number, Record<string, unknown>> = {};
      for (const rev of wanted) {
        try {
          const r = await getSubmissionRevision(submission.id, rev);
          loaded[rev] = r.revision.data;
        } catch { /* a revision that will not load simply shows no diff */ }
      }
      if (cancelled) return;
      setSnapshots(loaded);

      const out: Record<number, { key: string; before: unknown; after: unknown }[]> = {};
      for (const rev of wanted) {
        const after = loaded[rev];
        const before = loaded[rev - 1];
        if (!after || !before) continue;
        const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
        out[rev] = [...keys]
          .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
          .map((k) => ({ key: k, before: before[k], after: after[k] }));
      }
      setDiffs(out);
    })();
    return () => { cancelled = true; };
  }, [submission.id, revisions]);

  const submitter = submission.submitter as Submitter | null;

  const entries: Entry[] = [
    ...revisions.map((r): Entry => ({
      at: r.created_at,
      kind: r.revision === 1 ? 'original' : 'edit',
      title: r.revision === 1
        ? 'Submitted · revision 1'
        : r.author === 'respondent'
          ? `Edited by the respondent · revision ${r.revision}`
          : `Answers edited · revision ${r.revision}`,
      who: r.revision === 1 || r.author === 'respondent'
        ? `${submitter?.name || submitter?.email || 'The respondent'}${submitter?.providerName ? ` (SSO · ${submitter.providerName})` : ''} · ${formatFull(r.created_at)}`
        : `${r.changed_by_email || 'someone'} · ${formatFull(r.created_at)}`,
      revision: r.revision,
    })),
    ...deliveries.map((d): Entry => ({
      at: d.created_at,
      kind: d.status === 'success' ? 'delivered' : d.status === 'failed' ? 'failed' : 'other',
      title: d.triggered_by
        ? `Re-sent to ${d.connection_type}`
        : d.status === 'success' ? `Delivered · ${d.connection_type}`
          : d.status === 'failed' ? `Delivery failed · ${d.connection_type}`
            : `${d.status} · ${d.connection_type}`,
      who: `${d.triggered_by ? 'by an admin' : 'automatic'} · ${formatFull(d.created_at)}${d.attempts > 1 ? ` · attempt ${d.attempts}` : ''}`,
      error: d.last_error || undefined,
    })),
    ...notes.map((n): Entry => ({
      at: n.created_at,
      kind: 'note',
      title: 'Note',
      who: `${n.author?.name || n.author?.email || 'a former user'} · ${formatFull(n.created_at)}`,
      body: n.body,
      noteId: n.id,
      canDelete: n.canDelete,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const show = (v: unknown): string => {
    if (v === undefined) return '(nothing)';
    if (v === null || v === '') return '(empty)';
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  };

  return (
    <div className="sl-tl">
      {entries.map((e, i) => (
        <div className="sl-tl-item" key={e.kind === 'note' ? `note-${e.noteId}` : i} data-kind={e.kind}>
          <span className={`sl-tl-dot ${e.kind}`} />
          <div className="sl-tl-title">{e.title}</div>
          <div className="sl-tl-who">{e.who}</div>
          {e.error && (
            <div className="sl-tl-error">
              {e.error}
            </div>
          )}

          {e.kind === 'note' && (
            <>
              <div className="sl-note-body">{e.body}</div>
              {e.canDelete && onDeleteNote && e.noteId !== undefined && (
                <a
                  href="#"
                  className="sl-note-delete"
                  onClick={(ev) => { ev.preventDefault(); onDeleteNote(e.noteId!); }}
                >
                  Delete note
                </a>
              )}
            </>
          )}

          {e.kind === 'edit' && e.revision && diffs[e.revision]?.length > 0 && (
            <>
              <div className="sl-diff">
                {diffs[e.revision].map((d) => (
                  <div key={d.key}>
                    <div className="rm"><code>{d.key}</code> − {show(d.before)}</div>
                    <div className="add"><code>{d.key}</code> + {show(d.after)}</div>
                  </div>
                ))}
              </div>
              {onRestore && snapshots[e.revision - 1] && (
                <a
                  href="#"
                  style={{ fontSize: 12.5, display: 'inline-block', marginTop: 6 }}
                  onClick={(ev) => { ev.preventDefault(); onRestore(snapshots[e.revision! - 1]); }}
                >
                  Restore this value
                </a>
              )}
            </>
          )}

          {e.kind === 'original' && (
            <div className="sl-tl-note">
              {revisions.length > 1
                ? 'The answers as they arrived — the diffs above are what changed since.'
                : 'Nothing has been edited since.'}
            </div>
          )}
        </div>
      ))}
      {entries.length === 0 && <div className="sl-quiet">Nothing recorded yet.</div>}
    </div>
  );
}
